import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { BaseModal } from '../../UI/Modal/BaseModal';
import ExitWithoutSavingModal from '../../UI/V3/Modals/ExitWithoutSavingModal';
import { Location } from '../../../model/location';
import { DuplicateLocationDetailsStep } from './DuplicateLocationDetailsStep';
import { DuplicateLocationChecklistStep } from './DuplicateLocationChecklistStep';
import { DuplicateLocationProgress } from './DuplicateLocationProgress';
import { DuplicateLocationResult } from './DuplicateLocationResult';
import { useDuplicateLocation } from './useDuplicateLocation';
import styles from './DuplicateLocationModal.module.scss';

interface DuplicateLocationModalProps {
    isOpen: boolean;
    location: Location | null;
    onClose: () => void;
    // Called when a new location exists or may appear soon, so the caller can refresh its data
    onFinished?: () => void;
    onViewLocation: (locationId: number) => void;
}

export const DuplicateLocationModal: FunctionComponent<DuplicateLocationModalProps> = ({
    isOpen,
    location,
    onClose,
    onFinished,
    onViewLocation
}) => {
    const {
        step,
        details,
        areas,
        fieldErrors,
        generalError,
        result,
        updateDetail,
        toggleArea,
        toggleAll,
        goToChecklist,
        goBack,
        submit,
        retry,
        reset
    } = useDuplicateLocation(location);

    const [showExitPrompt, setShowExitPrompt] = useState(false);

    // Starts every opening from the source location's data
    useEffect(() => {
        if (isOpen) reset();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    useEffect(() => {
        if (step === 'completed') onFinished?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    const closeModal = useCallback(() => {
        // The duplication keeps running on the server, the new location may appear at any moment
        if (step === 'submitting' || step === 'running' || step === 'stalled') {
            onFinished?.();
        }

        reset();
        setShowExitPrompt(false);
        onClose();
    }, [step, onFinished, reset, onClose]);

    const handleClose = useCallback(() => {
        if (step === 'details' || step === 'checklist') {
            setShowExitPrompt(true);
        } else {
            closeModal();
        }
    }, [step, closeModal]);

    const handleViewLocation = useCallback(
        (locationId: number) => {
            reset();
            onClose();
            onViewLocation(locationId);
        },
        [reset, onClose, onViewLocation]
    );

    if (!location) return null;

    const newLocationName = details.businessName.trim();

    const renderStep = () => {
        switch (step) {
            case 'details':
                return (
                    <DuplicateLocationDetailsStep
                        sourceName={location.businessName}
                        details={details}
                        fieldErrors={fieldErrors}
                        generalError={generalError}
                        onChange={updateDetail}
                        onContinue={goToChecklist}
                    />
                );
            case 'checklist':
            case 'submitting':
                return (
                    <DuplicateLocationChecklistStep
                        areas={areas}
                        isSubmitting={step === 'submitting'}
                        onToggleArea={toggleArea}
                        onToggleAll={toggleAll}
                        onBack={goBack}
                        onSubmit={submit}
                    />
                );
            case 'running':
            case 'stalled':
                return (
                    <DuplicateLocationProgress
                        locationName={newLocationName}
                        stalled={step === 'stalled'}
                        onClose={closeModal}
                    />
                );
            case 'completed':
            case 'failed':
                return result ? (
                    <DuplicateLocationResult
                        locationName={newLocationName}
                        result={result}
                        onViewLocation={handleViewLocation}
                        onBackToForm={retry}
                        onClose={closeModal}
                    />
                ) : null;
        }
    };

    return (
        <>
            <BaseModal
                isOpen={isOpen}
                onClose={handleClose}
                className={styles.modal}
            >
                <BaseModal.Header
                    showLogo={true}
                    showCloseButton={true}
                />

                <BaseModal.Content className={styles.modalContent}>
                    <div className={styles.container}>
                        <form
                            className={styles.form}
                            onSubmit={e => e.preventDefault()}
                        >
                            {renderStep()}
                        </form>
                    </div>
                </BaseModal.Content>
            </BaseModal>
            {showExitPrompt && (
                <ExitWithoutSavingModal
                    onDiscard={closeModal}
                    onKeepEditing={() => setShowExitPrompt(false)}
                />
            )}
        </>
    );
};

export default DuplicateLocationModal;
